import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, RefreshCw, CheckCircle, XCircle,
  Eye, EyeOff, Send, ExternalLink, Settings
} from 'lucide-react';
import { SiTwilio } from 'react-icons/si';

const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export default function CommsChannels() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emailConfig, setEmailConfig] = useState(null);
  const [smsConfig, setSmsConfig] = useState(null);
  
  // Dialog states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testChannel, setTestChannel] = useState(null);
  const [testRecipient, setTestRecipient] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Form states
  const [emailForm, setEmailForm] = useState({ api_key: '', from_email: '', from_name: '' });
  const [smsForm, setSmsForm] = useState({ enabled: false, account_sid: '', auth_token: '', sender_phone: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadChannels();
  }, [user, navigate]);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const [emailRes, smsRes] = await Promise.all([
        axios.get(`${API}/platform/email-config`, getAuthConfig()).catch(() => ({ data: null })),
        axios.get(`${API}/platform/sms-config`, getAuthConfig()).catch(() => ({ data: null }))
      ]);
      setEmailConfig(emailRes.data);
      setSmsConfig(smsRes.data);
      
      if (emailRes.data) {
        setEmailForm({
          api_key: emailRes.data.api_key || '',
          from_email: emailRes.data.from_email || '',
          from_name: emailRes.data.from_name || ''
        });
      }
      if (smsRes.data) {
        setSmsForm({
          enabled: smsRes.data.enabled || false,
          account_sid: smsRes.data.account_sid || '',
          auth_token: smsRes.data.auth_token || '',
          sender_phone: smsRes.data.sender_phone || ''
        });
      }
    } catch (error) {
      console.error('Failed to load channels:', error);
      toast.error('Failed to load channel configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveEmailConfig = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/platform/email-config`, emailForm, getAuthConfig());
      toast.success('Email configuration saved');
      setShowEmailDialog(false);
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const saveSmsConfig = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/platform/sms-config`, smsForm, getAuthConfig());
      toast.success('SMS configuration saved');
      setShowSmsDialog(false);
      loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMS configuration');
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = (channel) => {
    setTestChannel(channel);
    setTestRecipient('');
    setTestMessage(channel === 'email' 
      ? 'This is a test email from Survivor Fantasy League.'
      : '🏝️ Test SMS from Survivor Fantasy League!'
    );
    setShowTestDialog(true);
  };

  const sendTest = async () => {
    if (!testRecipient) {
      toast.error(`Please enter a ${testChannel === 'email' ? 'email address' : 'phone number'}`);
      return;
    }
    
    try {
      setSending(true);
      if (testChannel === 'email') {
        await axios.post(`${API}/platform/email/test`, {
          to_email: testRecipient,
          subject: 'Test Email',
          message: testMessage
        }, getAuthConfig());
      } else {
        await axios.post(`${API}/platform/sms/test`, {
          phone: testRecipient,
          message: testMessage
        }, getAuthConfig());
      }
      toast.success(`Test ${testChannel === 'email' ? 'email' : 'SMS'} sent!`);
      setShowTestDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send test ${testChannel}`);
    } finally {
      setSending(false);
    }
  };

  const ChannelCard = ({ name, icon: Icon, brandIcon: BrandIcon, connected, provider, description, onConfigure, onTest }) => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              connected ? 'bg-emerald-500/10' : 'bg-zinc-800'
            }`}>
              {BrandIcon ? (
                <BrandIcon className={`w-7 h-7 ${connected ? 'text-emerald-400' : 'text-zinc-500'}`} />
              ) : (
                <Icon className={`w-7 h-7 ${connected ? 'text-emerald-400' : 'text-zinc-500'}`} />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-zinc-100">{name}</h3>
                <Badge className={`text-[10px] ${
                  connected 
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                    : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                }`}>
                  {connected ? 'Connected' : 'Not Configured'}
                </Badge>
              </div>
              <p className="text-sm text-zinc-500 mt-0.5">{provider}</p>
            </div>
          </div>
        </div>

        <p className="text-sm text-zinc-400 mb-4">{description}</p>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onConfigure}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configure
          </Button>
          {connected && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onTest}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Test
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin/comms">
      <div className="space-y-6" data-testid="comms-channels">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Channels</h1>
            <p className="text-sm text-zinc-500">Configure delivery infrastructure for notifications</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadChannels}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading channels...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Channel */}
            <ChannelCard
              name="Email"
              icon={Mail}
              connected={emailConfig?.is_configured}
              provider="SendGrid"
              description="Transactional email delivery for notifications, invites, and announcements."
              onConfigure={() => setShowEmailDialog(true)}
              onTest={() => openTestDialog('email')}
            />

            {/* SMS Channel */}
            <ChannelCard
              name="SMS"
              icon={MessageSquare}
              brandIcon={SiTwilio}
              connected={smsConfig?.enabled}
              provider="Twilio"
              description="Text message delivery for time-sensitive notifications and reminders."
              onConfigure={() => setShowSmsDialog(true)}
              onTest={() => openTestDialog('sms')}
            />
          </div>
        )}

        {/* Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-sm text-zinc-500">
              Channels are the delivery infrastructure for notifications. If a channel is not configured, 
              notifications using that channel will be blocked until configured.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Email Config Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Email Configuration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">SendGrid API Key</Label>
              <div className="relative mt-2">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={emailForm.api_key}
                  onChange={(e) => setEmailForm({ ...emailForm, api_key: e.target.value })}
                  placeholder="SG.xxxxxxxx"
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 pr-10 font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 h-7 w-7 p-0"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">From Email</Label>
              <Input
                type="email"
                value={emailForm.from_email}
                onChange={(e) => setEmailForm({ ...emailForm, from_email: e.target.value })}
                placeholder="notifications@yourdomain.com"
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
              />
            </div>
            <div>
              <Label className="text-zinc-300">From Name</Label>
              <Input
                value={emailForm.from_name}
                onChange={(e) => setEmailForm({ ...emailForm, from_name: e.target.value })}
                placeholder="Survivor Fantasy League"
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
              />
            </div>
            <a 
              href="https://app.sendgrid.com/settings/api_keys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
            >
              Get SendGrid API Key
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEmailDialog(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button onClick={saveEmailConfig} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-zinc-900">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Config Dialog */}
      <Dialog open={showSmsDialog} onOpenChange={setShowSmsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <SiTwilio className="w-5 h-5 text-red-400" />
              SMS Configuration (Twilio)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
              <div>
                <Label className="text-zinc-100">Enable SMS</Label>
                <p className="text-xs text-zinc-500">Turn on SMS delivery</p>
              </div>
              <Switch
                checked={smsForm.enabled}
                onCheckedChange={(checked) => setSmsForm({ ...smsForm, enabled: checked })}
              />
            </div>
            <div>
              <Label className="text-zinc-300">Account SID</Label>
              <Input
                value={smsForm.account_sid}
                onChange={(e) => setSmsForm({ ...smsForm, account_sid: e.target.value })}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100 font-mono"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Auth Token</Label>
              <div className="relative mt-2">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  value={smsForm.auth_token}
                  onChange={(e) => setSmsForm({ ...smsForm, auth_token: e.target.value })}
                  placeholder="Your Auth Token"
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 pr-10 font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 h-7 w-7 p-0"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Phone Number</Label>
              <Input
                value={smsForm.sender_phone}
                onChange={(e) => setSmsForm({ ...smsForm, sender_phone: e.target.value })}
                placeholder="+1234567890"
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100 font-mono"
              />
              <p className="text-xs text-zinc-500 mt-1">E.164 format</p>
            </div>
            <a 
              href="https://console.twilio.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
            >
              Twilio Console
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSmsDialog(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button onClick={saveSmsConfig} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-zinc-900">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">
              Send Test {testChannel === 'email' ? 'Email' : 'SMS'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">
                {testChannel === 'email' ? 'Recipient Email' : 'Recipient Phone'}
              </Label>
              <Input
                type={testChannel === 'email' ? 'email' : 'tel'}
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                placeholder={testChannel === 'email' ? 'test@example.com' : '+1234567890'}
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Message</Label>
              <Input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTestDialog(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button onClick={sendTest} disabled={sending} className="bg-amber-500 hover:bg-amber-600 text-zinc-900">
              {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
